import * as React from 'react'
import { useState, useEffect } from 'react'
import clx from 'classnames'

import { useClickOutside } from '@/app/hooks/useClickOutside'
import { ChevronDownIcon } from '@/app/features/icons/ChevronDown'
import { CheckIcon } from '@/app/features/icons/Check'

export type Option = {
  key?: string | number
  label: string
  value: string
  content?: React.ReactNode
  icon?: React.ElementType
}

type Props = {
  label?: string
  placeholder?: string
  icon?: React.ElementType
  options: Array<Option>
  className?: string
  value?: string[]
  error?: string | boolean
  handleChange?: (
    values: string[],
    props: { name: string; options: Option[] },
  ) => void
  name?: string
  disabled?: boolean
  searchable?: boolean
  searchPlaceholder?: string
  favoriteValues?: string[]
  onToggleFavorite?: (value: string) => void
  showSelectAll?: boolean
  listMaxHeight?: number
  showListScrollbar?: boolean
  children?: React.ReactNode
}

export const FieldsetMultiSelect = ({
  label,
  value = [],
  options,
  name,
  placeholder = 'Wybierz...',
  icon: Icon = null,
  disabled,
  handleChange,
  error,
  className,
  searchable = true,
  searchPlaceholder = 'Szukaj...',
  favoriteValues,
  onToggleFavorite,
  showSelectAll = false,
  listMaxHeight = 200,
  showListScrollbar = false,
  children,
}: Props) => {
  // Internal selection state (like legacy MultiDropdown's 'selection' state)
  const [selection, setSelection] = useState<string[]>(value || [])
  const [open, setOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const [searchTerm, setSearchTerm] = useState('')
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  const favoriteSet = React.useMemo(
    () => new Set(favoriteValues || []),
    [favoriteValues],
  )

  const visibleOptions = React.useMemo(() => {
    let result = options
    if (searchable && searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase()
      result = options.filter((o) =>
        (o.label || o.value || '').toLowerCase().includes(term),
      )
    }
    // Pin favorites to the top (stable order within each group).
    if (favoriteSet.size > 0) {
      const favs = result.filter((o) => favoriteSet.has(o.value))
      const rest = result.filter((o) => !favoriteSet.has(o.value))
      result = [...favs, ...rest]
    }
    return result
  }, [options, searchable, searchTerm, favoriteSet])
  // Keep internal selection in sync with the external `value` prop.
  // We compare against the *current* selection (not a "previous prop" ref) so an
  // external update — e.g. clicking "Zastosuj ulubione" — always takes effect, even
  // right after a blur that pushed an unchanged value back to the parent (which would
  // otherwise leave a stale guard flag set and swallow the next update).
  useEffect(() => {
    const next = value || []
    setSelection((prev) =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next,
    )
  }, [value])

  const selectedOptions =
    options?.filter((o) => selection.includes(o.value)) || []

  const notifyParent = React.useCallback(() => {
    if (handleChange) {
      const selectedOpts = options.filter((o) => selection.includes(o.value))
      handleChange(selection, { name: name || '', options: selectedOpts })
    }
  }, [handleChange, selection, options, name])

  const toggle = () => {
    if (!disabled) {
      const willClose = open
      setOpen(!open)

      // When closing, notify parent of current selection (like legacy MultiDropdown)
      if (willClose) {
        notifyParent()
      } else {
        setSearchTerm('')
        setFocusedIndex(-1)
      }
    }
  }

  const toggleOption = (option: Option) => {
    // Update internal selection state immediately (like legacy)
    const newSelection = selection.includes(option.value)
      ? selection.filter((v) => v !== option.value)
      : [...selection, option.value]

    setSelection(newSelection)
    // Don't call handleChange here - only when dropdown closes
  }

  const allVisibleSelected =
    visibleOptions.length > 0 &&
    visibleOptions.every((o) => selection.includes(o.value))

  const toggleSelectAll = () => {
    const visibleValues = visibleOptions.map((o) => o.value)
    if (allVisibleSelected) {
      const visibleSet = new Set(visibleValues)
      setSelection((prev) => prev.filter((v) => !visibleSet.has(v)))
    } else {
      setSelection((prev) => Array.from(new Set([...prev, ...visibleValues])))
    }
  }

  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const listRef = React.useRef<HTMLUListElement>(null)
  const [dropdownWidth, setDropdownWidth] = React.useState<number | undefined>(
    undefined,
  )

  useClickOutside({
    ref: dropdownRef,
    handler: () => {
      if (open) {
        setOpen(false)
        setSearchTerm('')
        notifyParent()
      }
    },
  })

  // Focus the search input when the dropdown opens
  useEffect(() => {
    if (open && searchable) {
      searchInputRef.current?.focus()
    }
  }, [open, searchable])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key.length === 1 && /[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/.test(e.key)) {
        setOpen(true)
        setSearchTerm('')
        setFocusedIndex(-1)
      }
      return
    }

    const count = visibleOptions.length

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (count > 0) setFocusedIndex((prev) => (prev + 1) % count)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (count > 0) setFocusedIndex((prev) => (prev - 1 + count) % count)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (focusedIndex >= 0 && focusedIndex < count) {
        toggleOption(visibleOptions[focusedIndex])
      }
    } else if (!searchable && e.key.length === 1) {
      // Legacy letter-jump (only when there is no search box)
      const letter = e.key.toLowerCase()
      const foundIndex = visibleOptions.findIndex((o) =>
        o.label.toLowerCase().startsWith(letter),
      )
      if (foundIndex >= 0) {
        setFocusedIndex(foundIndex)
        const li = listRef.current?.children[foundIndex] as HTMLElement
        li?.scrollIntoView({ block: 'nearest' })
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
      setSearchTerm('')
    }
  }

  useEffect(() => {
    if (open && focusedIndex >= 0) {
      const li = listRef.current?.children[focusedIndex] as HTMLElement
      li?.scrollIntoView({ block: 'nearest' })
    }
  }, [focusedIndex, open])

  // Calculate dropdown width based on longest item text
  useEffect(() => {
    if (open && listRef.current) {
      // Create a temporary hidden element to measure text width
      const measureEl = document.createElement('span')
      measureEl.style.visibility = 'hidden'
      measureEl.style.position = 'absolute'
      measureEl.style.whiteSpace = 'nowrap'
      measureEl.style.fontSize = '14px' // text-sm
      measureEl.style.fontFamily = 'system-ui, -apple-system, sans-serif'
      document.body.appendChild(measureEl)

      let maxWidth = 0

      options.forEach((option) => {
        const text = option.content?.toString() || option.label || option.value
        measureEl.textContent = text
        const width = measureEl.offsetWidth
        maxWidth = Math.max(maxWidth, width)
      })

      document.body.removeChild(measureEl)

      // Add padding for checkbox (16px) + gap (8px) + icon (12px) + horizontal padding (24px)
      const padding = 60
      setDropdownWidth(Math.max(maxWidth + padding, 200)) // Minimum 200px
    }
  }, [open, options])

  // Display logic for button text
  const getDisplayText = () => {
    if (selectedOptions.length === 0) {
      return null
    } else if (selectedOptions.length === 1) {
      return selectedOptions[0].label
    } else if (selectedOptions.length === 2) {
      return selectedOptions.map((o) => o.label).join(', ')
    } else {
      // For 3+ items, show count
      return `${selectedOptions.length} wybrane`
    }
  }

  const displayText = getDisplayText()

  return (
    <div>
      <input type="hidden" name={name} value={selection.join(',')} readOnly />
      {label && (
        <label className="tw-group tw-mb-0 tw-px-1 tw-pb-0.5 tw-text-sm tw-font-medium">
          {label}
        </label>
      )}
      <div
        className={clx('tw-relative tw-w-full', className)}
        ref={dropdownRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls="dropdown-list"
        aria-multiselectable="true"
      >
        <button
          type="button"
          onClick={toggle}
          className={clx(
            'tw-flex tw-h-9 tw-w-full tw-items-center tw-justify-between tw-gap-1 tw-rounded-md tw-border tw-px-3 tw-py-1.5 tw-text-left tw-transition-all focus:tw-ring-2 focus:tw-ring-focus-100 focus:tw-ring-offset-2',
            !open && 'hover:tw-opacity-80',
            {
              '[&:not(:focus)]:tw-border-error': error,
            },
          )}
        >
          <span className="tw-flex tw-items-center tw-gap-1 tw-font-medium">
            {Icon && (
              <span className="tw-p-0.5">
                <Icon className="tw-h-4 tw-w-4" />
              </span>
            )}
            {displayText ? (
              <span className="tw-text-sm">{displayText}</span>
            ) : (
              <span className="tw-text-sm tw-font-normal tw-text-input">
                {placeholder}
              </span>
            )}
          </span>
          <ChevronDownIcon
            className={`tw-h-5 tw-w-5 tw-transition-transform ${open ? 'tw-rotate-180' : ''}`}
          />
        </button>

        {open && (
          <div
            className="tw-absolute tw-z-10 tw-mt-2 tw-flex tw-flex-col tw-rounded-md tw-border tw-border-content-base tw-border-opacity-20 tw-bg-white tw-text-content-base tw-shadow-multi"
            style={{
              width: dropdownWidth ? `${dropdownWidth}px` : 'auto',
              minWidth: '200px',
            }}
          >
            {searchable && (
              <div className="tw-border-b tw-border-content-base tw-border-opacity-10 tw-p-1.5">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  placeholder={searchPlaceholder}
                  onChange={(e) => {
                    setSearchTerm(e.target.value)
                    setFocusedIndex(-1)
                  }}
                  className="tw-h-8 tw-w-full tw-rounded tw-border tw-border-content-base tw-border-opacity-20 tw-bg-white tw-px-2 tw-text-sm tw-text-content-base focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-focus-100"
                />
              </div>
            )}
            {showSelectAll && visibleOptions.length > 0 && (
              <div className="tw-border-b tw-border-content-base tw-border-opacity-10 tw-px-1.5 tw-py-1">
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-[4px] tw-px-1 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base hover:tw-bg-base-300 hover:tw-bg-opacity-80 focus:tw-outline-none"
                >
                  <span
                    className={clx(
                      'tw-flex tw-h-4 tw-w-4 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded tw-border-2 tw-transition-colors',
                      allVisibleSelected
                        ? 'tw-border-focus-100 tw-bg-focus-100'
                        : 'tw-border-content-base tw-border-opacity-30',
                    )}
                  >
                    {allVisibleSelected && (
                      <CheckIcon className="tw-h-3 tw-w-3 tw-text-white" />
                    )}
                  </span>
                  {allVisibleSelected ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
                </button>
              </div>
            )}
            <ul
              ref={listRef}
              className={clx(
                'tw-flex tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-px-1.5 tw-py-1.5',
                showListScrollbar
                  ? 'dropdown-scrollbar'
                  : 'custom-scrollbar hide-scrollbar',
              )}
              style={{ maxHeight: listMaxHeight }}
            >
              {visibleOptions.length === 0 && (
                <li className="tw-px-1 tw-py-2 tw-text-sm tw-text-content-base tw-text-opacity-60">
                  {searchTerm ? 'Brak wyników' : placeholder}
                </li>
              )}
              {visibleOptions.map((option, idx) => {
              const isSelected = selection.includes(option.value)
              const isFavorite = favoriteSet.has(option.value)
              return (
                <li
                  key={option.value}
                  className={clx(
                    'tw-flex tw-items-center tw-rounded-[4px]',
                    isSelected && '!tw-bg-secondary tw-bg-opacity-80',
                    focusedIndex === idx && 'tw-bg-base-300 tw-bg-opacity-80',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => toggleOption(option)}
                    className={clx(
                      'tw-flex tw-min-w-0 tw-flex-1 tw-cursor-pointer tw-items-center tw-justify-between tw-rounded-[4px] tw-px-1 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-base-300 hover:tw-bg-opacity-80 focus:tw-bg-base-300 focus:tw-bg-opacity-80 focus:tw-outline-none',
                      isSelected && 'tw-font-medium',
                    )}
                  >
                    <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
                      {/* Checkbox indicator */}
                      <span
                        className={clx(
                          'tw-flex tw-h-4 tw-w-4 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded tw-border-2 tw-transition-colors',
                          isSelected
                            ? 'tw-border-focus-100 tw-bg-focus-100'
                            : 'tw-border-content-base tw-border-opacity-30',
                        )}
                      >
                        {isSelected && (
                          <CheckIcon className="tw-h-3 tw-w-3 tw-text-white" />
                        )}
                      </span>
                      {option.icon && (
                        <span className="tw-p-1">
                          <option.icon className="tw-h-3" />
                        </span>
                      )}
                      <span className="tw-truncate">
                        {option.content || option.label || option.value}
                      </span>
                    </span>
                  </button>
                  {onToggleFavorite && (
                    <button
                      type="button"
                      title={
                        isFavorite
                          ? 'Usuń z ulubionych'
                          : 'Dodaj do ulubionych'
                      }
                      aria-label={
                        isFavorite
                          ? 'Usuń z ulubionych'
                          : 'Dodaj do ulubionych'
                      }
                      onClick={(e) => {
                        e.stopPropagation()
                        onToggleFavorite(option.value)
                      }}
                      className={clx(
                        'tw-flex tw-h-7 tw-w-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded tw-text-base tw-leading-none tw-transition-colors hover:tw-bg-base-300',
                        isFavorite
                          ? 'tw-text-yellow-500'
                          : 'tw-text-content-base tw-text-opacity-40',
                      )}
                    >
                      {isFavorite ? '★' : '☆'}
                    </button>
                  )}
                </li>
              )
            })}
            </ul>
          </div>
        )}
        {name && (
          <input type="hidden" name={name} value={selection.join(',')} />
        )}
      </div>
      {error && typeof error === 'string' && (
        <span className="tw-group-focus:tw-opacity-0 tw-text-xs tw-font-medium tw-text-error">
          {error}
        </span>
      )}
      <div>{children}</div>
    </div>
  )
}
